/**
 * Pinia SuperAdmin-summary store (PR-2 of the SA design-parity chain).
 *
 * Hydrates the SA sidebar's per-row sub-text + status dot data from the
 * backend ``POST /api/superadmin/dashboard-summary`` endpoint
 * (api/src/endpoints/superadmin_dashboard.py). The endpoint returns a
 * single 8-key ``summary`` object; the layout binds each row to its
 * slot.
 *
 * Mirrors ``frontend/src/stores/distributorSummary.js`` exactly (same
 * shape, same epoch-guarded fetch / clear contract). The SA sidebar
 * does NOT participate in impersonation (a SuperAdmin sees /admin/*
 * with no effective tenant), so unlike the distributor summary store
 * there is no impersonation-stop hook calling clear() from the
 * impersonation store. clear() remains in the public API for symmetry
 * + future-proofing (logout flow, tab-switch optimizations).
 *
 * Endpoint URL is relative because @/services/api's baseURL ends in
 * ``/api``.
 *
 * Generic sidebar-refresh delegation (sidebar-emit chain PR-F, 2026-07-15,
 * https://trello.com/c/g9K52FsA): like distributorSummary / portalNavSummary,
 * this store SUBSCRIBES to the single ``sidebar:refresh`` event (see
 * stores/sidebarRefresh.js). An SA mutation emits
 * ``emitSidebarRefresh('<sa-row-id>')`` on success; ``subscribe()`` refetches
 * when that id is a row THIS layout owns (``SUPERADMIN_KEYS``). One mechanism
 * across all three shells; no bespoke per-page refresh plumbing.
 *
 * SA is the platform-admin scope: unlike distributorSummary there is no
 * impersonation tenant, cap gate, or stale-tenant guard here -- the SA sees
 * /admin/* with no effective tenant. The only relevance decision is "is this
 * id an SA sidebar row?", then the existing epoch-guarded fetch().
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'
import { useEventBus } from '@/stores/eventBus'
import { SIDEBAR_REFRESH_EVENT } from '@/stores/sidebarRefresh'

// The summary-store keys the SA sidebar owns -- the ``id`` values on a
// ``sidebar:refresh`` event that are RELEVANT to this layout. These are exactly
// the key-name values SuperAdminLayout renders (see its summary key table). The
// dashboard-summary is ONE endpoint returning every SA counter, so ANY owned id
// changing means a single coarse refetch -- the id is only a relevance token.
// Kept as the FULL SA key set (not just the currently-emitted subset) so a
// future SA counter that starts emitting needs no subscribe-side change here.
const SUPERADMIN_KEYS = Object.freeze(
    new Set([
        'dashboard',
        'distributors',
        // SHARED KEY: 'clients' is also a DISTRIBUTOR_KEYS entry
        // (stores/distributorSummary.js). Both shells own a 'clients' row, so a
        // 'clients' emit is relevant to whichever shell is mounted. This is safe
        // only because SuperAdminLayout and DistributorLayout are route-exclusive
        // (never co-mounted) and each tears down its subscription on unmount --
        // NOT because of a tenant filter (a true-SA emit carries tenantId:null,
        // which the distributor stale-tenant guard does not drop). Re-audit this
        // overlap if the shells can ever co-mount or the guard changes.
        'clients',
        'risk_watch',
        'plans',
        'money',
        'access_control',
        'operations',
        'audit',
    ]),
)

export const useSuperAdminSummaryStore = defineStore('superAdminSummary', () => {
    // ---- state ----
    const summary = ref(null)
    // metrics: structured page-body KPIs (from the same POST endpoint).
    // Shape: { dashboard: {...}, onboarding_funnel: [{stage, n}, ...] }
    const metrics = ref(null)
    const loading = ref(false)
    const error = ref(null)
    // Epoch guard: bumped on clear() so an in-flight fetch cannot
    // repopulate the store after clear() resets it. Same race-guard
    // pattern as distributorSummary (bar-raiser P2 on PR #1402).
    let epoch = 0

    // ---- actions ----
    async function fetch() {
        const myEpoch = epoch
        loading.value = true
        error.value = null
        try {
            const res = await api.post('/superadmin/dashboard-summary')
            if (myEpoch !== epoch) return
            summary.value = res.data.summary
            metrics.value = res.data.metrics || null
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e.message || 'fetch failed'
            summary.value = null
            metrics.value = null
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    function clear() {
        epoch += 1
        summary.value = null
        metrics.value = null
        error.value = null
        loading.value = false
    }

    /**
     * Event-driven refresh entry point (generic sidebar:refresh delegation).
     *
     * Delegates to the existing epoch-guarded ``fetch()`` -- it is deliberately
     * NOT a raw refetch: epoch safety is inherited, so a response that arrives
     * after a ``clear()`` is still dropped. SA has no tenant / impersonation /
     * cap guard (platform-admin scope), so by the time we are here the id is
     * already known-relevant (the subscribe seam gated on SUPERADMIN_KEYS).
     */
    function maybeRefetch() {
        fetch()
    }

    /**
     * Register the SA summary's refresh handler on the event bus and return the
     * teardown fn. Called once from SuperAdminLayout's ``onMounted``; torn down
     * on ``onUnmounted`` so no listener survives a shell switch.
     *
     * Delegation: subscribes to the SINGLE ``sidebar:refresh`` event and only
     * refetches when the event's ``id`` is a row THIS layout owns
     * (``SUPERADMIN_KEYS``). The id is the relevance token -- a new SA counter
     * that mutates an existing summary key just emits ``sidebar:refresh`` with
     * that key and this handler already routes it, no subscribe-side change.
     */
    function subscribe() {
        const bus = useEventBus()
        return bus.on(SIDEBAR_REFRESH_EVENT, (payload) => {
            if (!SUPERADMIN_KEYS.has(payload?.id)) return
            maybeRefetch()
        })
    }

    return {
        summary,
        metrics,
        loading,
        error,
        fetch,
        clear,
        maybeRefetch,
        subscribe,
    }
})
